import { Button, Input, Modal, Select } from '@sjuni/ui';
import { CornerDownRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { TranslatableField } from '@/components/form/TranslatableField';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';
import { ResourceToolbar } from '@/components/list/ResourceToolbar';
import { useResourceAction, useResourceList, useSaveResource } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';

import type { DocumentCategoryInput, DocumentCategoryRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/document-categories`.
 *
 * Unlike the news taxonomy these nest, and the list shows it: a child is
 * indented under the name of its parent rather than listed as a peer with a
 * column nobody reads. §6's public library renders the same tree, so an editor
 * arranging it here should be looking at the shape they are arranging.
 */
export function DocumentCategoriesPage() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const { can } = useAuth();
  const list = useListParams('sort_order');

  const [editing, setEditing] = useState<DocumentCategoryRow | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<DocumentCategoryRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, isPending, isError } = useResourceList<DocumentCategoryRow>(
    'document-categories',
    list.params,
  );

  const act = useResourceAction<DocumentCategoryRow>('document-categories');

  const mayManage = can('documents.manage-taxonomy');

  const columns: Column<DocumentCategoryRow>[] = [
    {
      key: 'name',
      label: t('documents.categories.columns.name'),
      render: (row) => (
        <span className="inline-flex items-center gap-1.5">
          {row.parent ? (
            <>
              <CornerDownRight
                size={14}
                strokeWidth={1.75}
                aria-hidden="true"
                className="ms-4 text-muted"
              />
              {/* The parent's name is announced rather than only drawn: the
                  indent is invisible to a screen reader, and "which category
                  is this under" is the question the column answers. */}
              <span className="sr-only">
                {t('documents.categories.under', { parent: valueFor(row.parent.name, locale) })}
              </span>
            </>
          ) : null}

          <span className="font-semibold text-primary">{valueFor(row.name, locale)}</span>
        </span>
      ),
    },
    {
      key: 'slug',
      label: t('news.categories.columns.slug'),
      render: (row) => (
        <code className="text-caption text-secondary">{valueFor(row.slug, locale)}</code>
      ),
    },
    {
      key: 'translations',
      label: t('news.columns.translations'),
      render: (row) => (
        <span className="text-caption uppercase text-secondary">
          {row.translations_available.join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'documents_count',
      label: t('documents.categories.columns.count'),
      align: 'end',
      render: (row) => (row.documents_count === null ? '—' : row.documents_count.toLocaleString()),
    },
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) =>
        mayManage ? (
          <span className="inline-flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Pencil size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                setEditing(row);
              }}
            >
              {t('news.categories.edit')}
            </Button>

            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                setDeleteError(null);
                setDeleting(row);
              }}
            >
              {t('common.delete')}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.documentCategories')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('documents.categories.intro')}</p>
      </div>

      {isError ? <FormError message={t('errors.generic')} /> : null}
      <FormError message={deleteError} />

      <ResourceToolbar
        search={list.search}
        onSearchChange={list.setSearch}
        perPage={list.perPage}
        onPerPageChange={list.setPerPage}
        showTrashed={list.showTrashed}
        onShowTrashedChange={list.setShowTrashed}
        meta={data?.meta}
        buildPageHref={list.buildPageHref}
        primaryAction={
          mayManage ? (
            <Button
              type="button"
              size="sm"
              iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => setEditing(null)}
            >
              {t('documents.categories.add')}
            </Button>
          ) : null
        }
      />

      <ResourceTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={isPending}
        onOpen={mayManage ? (row) => setEditing(row) : undefined}
        emptyTitle={t('documents.categories.empty.title')}
        emptyBody={t('documents.categories.empty.body')}
      />

      {editing !== undefined ? (
        <CategoryDialog
          category={editing}
          all={data?.data ?? []}
          onClose={() => setEditing(undefined)}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={t('documents.categories.delete.title')}
        body={t('documents.categories.delete.body', {
          name: deleting ? valueFor(deleting.name, locale) : '',
        })}
        confirmLabel={t('common.delete')}
        // No `deleted_at` on this table, so there is nothing to restore and the
        // dialog must not imply there is.
        reversible={false}
        busy={act.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) {
            return;
          }

          act.mutate(
            { id: deleting.id, action: 'delete' },
            {
              // The 409 the server sends when the category still holds
              // documents or subcategories. Surfaced rather than swallowed:
              // the editor's next move is to move those first.
              onError: (error) => setDeleteError(error.message),
              onSettled: () => setDeleting(null),
            },
          );
        }}
      />
    </div>
  );
}

interface CategoryFields {
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  parent_id: number | '';
  sort_order: number;
}

function CategoryDialog({
  category,
  all,
  onClose,
}: {
  category: DocumentCategoryRow | null;
  /** The parent options. */
  all: DocumentCategoryRow[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const [formError, setFormError] = useState<string | null>(null);

  const save = useSaveResource<DocumentCategoryRow, DocumentCategoryInput>('document-categories');

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFields>({
    defaultValues: {
      name: category?.name ?? {},
      slug: category?.slug ?? {},
      description: category?.description ?? {},
      parent_id: category?.parent_id ?? '',
      sort_order: category?.sort_order ?? 0,
    },
  });

  const name = watch('name');
  const description = watch('description');

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await save.mutateAsync({
        id: category?.id,
        input: {
          name: values.name,
          slug: values.slug,
          description: values.description,
          parent_id: values.parent_id === '' ? null : Number(values.parent_id),
          sort_order: Number(values.sort_order),
        },
      });

      onClose();
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'name',
        'name.ka',
        'slug',
        'description',
        'parent_id',
        'sort_order',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={category ? t('news.categories.edit') : t('documents.categories.add')}
      closeLabel={t('common.close')}
      className="sm:w-[min(92vw,680px)]"
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <FormError message={formError} />

        <TranslatableField
          label={t('documents.categories.fields.name')}
          name="name"
          value={name}
          onChange={(next) => setValue('name', next, { shouldDirty: true })}
          error={errors.name?.message}
        />

        <TranslatableField
          label={t('documents.categories.fields.description')}
          name="description"
          multiline
          rows={2}
          value={description}
          onChange={(next) => setValue('description', next, { shouldDirty: true })}
          required={false}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('documents.categories.fields.parent')}
            hint={t('documents.categories.fields.parentHint')}
            {...register('parent_id')}
            error={errors.parent_id?.message}
            options={[
              { value: '', label: t('documents.categories.fields.parentNone') },
              ...all
                // A category cannot be its own parent, and the server refuses a
                // deeper cycle too — this only removes the one an editor is
                // most likely to reach for by accident.
                .filter((option) => option.id !== category?.id)
                .map((option) => ({
                  value: String(option.id),
                  label: valueFor(option.name, locale),
                })),
            ]}
          />

          <Input
            {...register('sort_order', { valueAsNumber: true })}
            type="number"
            min={0}
            label={t('documents.categories.fields.order')}
            hint={t('news.categories.fields.orderHint')}
            error={errors.sort_order?.message}
          />
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>

          <SubmitButton loading={isSubmitting}>{t('editor.publishing.save')}</SubmitButton>
        </div>
      </form>
    </Modal>
  );
}
